import React, { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { siteInfo, images } from '@/data/mock';
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { contactApi } from '@/lib/api';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const payload = {
      name: (formData.get('name') || '').trim(),
      email: (formData.get('email') || '').trim(),
      phone: (formData.get('phone') || '').trim() || null,
      subject: (formData.get('subject') || '').trim() || null,
      message: (formData.get('message') || '').trim(),
    };
    if (!payload.name || !payload.email || !payload.message) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setIsSubmitting(true);
    try {
      await contactApi.send(payload);
      toast.success('Votre message a bien été envoyé. Nous vous répondrons rapidement.');
      form.reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? "Veuillez vérifier les champs du formulaire."
          : "Une erreur est survenue. Merci de réessayer.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block text-sm uppercase tracking-wider text-[var(--color-gold)] font-medium mb-4">
              Contactez-nous
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--text-primary)] mb-6">
              Nous Contacter
            </h1>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto mb-8"></div>
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed">
              Vous avez des questions sur nos Maine Coons ? N'hésitez pas à nous contacter.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl text-[var(--text-primary)] mb-8">
                Informations de contact
              </h2>

              <div className="space-y-6">
                {/* Phone */}
                <a
                  href={`tel:${siteInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-start gap-4 p-6 bg-[var(--surface-cream)] rounded-xl hover:shadow-md transition-shadow group"
                >
                  <div className="feature-icon group-hover:bg-[var(--color-gold)] group-hover:text-white">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)] mb-1">
                      Téléphone
                    </h3>
                    <p className="text-lg text-[var(--color-gold)] font-medium">
                      {siteInfo.phone}
                    </p>
                    <p className="text-sm text-[var(--text-subtle)] mt-1">
                      Cliquez pour appeler
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="flex items-start gap-4 p-6 bg-[var(--surface-cream)] rounded-xl hover:shadow-md transition-shadow group"
                >
                  <div className="feature-icon group-hover:bg-[var(--color-gold)] group-hover:text-white">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)] mb-1">
                      Email
                    </h3>
                    <p className="text-lg text-[var(--color-gold)] font-medium">
                      {siteInfo.email}
                    </p>
                    <p className="text-sm text-[var(--text-subtle)] mt-1">
                      Cliquez pour envoyer un email
                    </p>
                  </div>
                </a>

                {/* Location */}
                <div className="flex items-start gap-4 p-6 bg-[var(--surface-cream)] rounded-xl">
                  <div className="feature-icon">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)] mb-1">
                      Localisation
                    </h3>
                    <p className="text-[var(--text-secondary)]">
                      Plounéour-Ménez<br />
                      Finistère (29), Bretagne
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4 p-6 bg-[var(--surface-cream)] rounded-xl">
                  <div className="feature-icon">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)] mb-1">
                      Horaires
                    </h3>
                    <p className="text-[var(--text-secondary)]">
                      Sur rendez-vous uniquement<br />
                      7j/7 selon disponibilités
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl text-[var(--text-primary)] mb-8">
                Envoyez-nous un message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      data-testid="contact-name-input"
                      className="form-input"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="contact-email-input"
                      className="form-input"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    className="form-input"
                    placeholder="06 00 00 00 00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Sujet</Label>
                  <Input
                    id="subject"
                    name="subject"
                    data-testid="contact-subject-input"
                    className="form-input"
                    placeholder="Renseignements sur les chatons disponibles..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    data-testid="contact-message-input"
                    className="form-input min-h-[150px]"
                    placeholder="Votre message..."
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="btn-primary w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer le message
                </Button>

                <p className="text-sm text-[var(--text-subtle)]">
                  * Champs obligatoires. En soumettant ce formulaire, vous acceptez notre 
                  <a href="/politique-confidentialite" className="text-[var(--color-gold)] hover:underline ml-1">
                    politique de confidentialité
                  </a>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-[var(--text-primary)] mb-4">
              Nous trouver
            </h2>
            <p className="text-[var(--text-secondary)]">
              Situés au cœur des Monts d'Arrée, dans le Finistère
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src={images.brittany1}
                alt="Paysage de Bretagne"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="map-container h-80">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d21095.36881252088!2d-3.8674799!3d48.4534799!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4816d4b4c5a5d0c7%3A0x40ca5cd36e4ab30!2s29410%20Ploun%C3%A9our-M%C3%A9nez!5e0!3m2!1sfr!2sfr!4v1699999999999!5m2!1sfr!2sfr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation Maison Bengal & Coon"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
